import { Landmark, Loader2, MoreHorizontal, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { addBankAccount } from "@/apis/custom/customRoutes";
import {
  type BankAccount,
  BankAccountType,
  type BankConnection,
} from "@/apis/gql/types";
import { BankReauthAlert } from "@/components/shared/alerts/bank-reauth-alert";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn, formatIban } from "@/lib/utils";
import { useTenant } from "@/providers/session";
import { Skeleton } from "@mui/material";
import { useParams } from "@tanstack/react-router";
import { toast } from "sonner";
import AccountCard from "./bank-account-card";

interface ConnectedBankCardProps {
  connection: BankConnection;
  onRemove: () => void;
  onSetDefaultAccount: (accountId: string, isCurrentlyDefault: boolean) => void;
  onSetJournalAccount: (journalAccountId: string) => Promise<void>;
}

export const BankConnectionCard = ({
  connection,
  onRemove,
  onSetDefaultAccount,
  onSetJournalAccount,
}: ConnectedBankCardProps) => {
  const { t } = useTranslation();
  const { company: companySlug } = useParams({ strict: false });
  const { tenantId } = useTenant();
  const [addingAccount, setAddingAccount] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const bank = connection.metadata.bank;

  return (
    <>
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden shadow-sm">
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-lg bg-white border border-gray-200 p-1.5 flex items-center justify-center overflow-hidden">
                {bank.icon?.url ? (
                  <img
                    src={bank.icon.url}
                    alt={bank.name}
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <Landmark className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
              <div>
                <h3 className="font-medium text-gray-900">
                  {connection.connectionName
                    ? `${connection.connectionName} - ${bank.name}`
                    : bank.name}
                </h3>
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                  <span className="font-mono">{bank.bic}</span>
                  <span className="h-1 w-1 rounded-full bg-gray-300" />
                  <span>
                    {`${t("bank_connections.created_at")}: ${formatDate(
                      connection.createdAt,
                    )}`}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="flex items-center text-red-600 cursor-pointer"
                    onClick={handleRemoveConnection}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    <span>{t("bank_connections.remove_connection")}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <BankReauthAlert
            className="mt-4 mb-2"
            onlyForBankConnectionId={connection.bankConnectionId}
          />
        </div>

        <div className="border-t border-gray-200">
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-900">
                {t("bank_connections.accounts")}
              </h4>
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1"
                onClick={handleAddBankAccount}
                disabled={addingAccount}
              >
                {addingAccount ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                <span className="text-xs">Hinzufügen</span>
              </Button>
            </div>
            <div className="space-y-2">
              {[...connection.accounts]
                .sort((a, b) => a.accountNumber.localeCompare(b.accountNumber))
                .map((account) => (
                  <AccountCard
                    key={account.bankAccountId}
                    account={account}
                    isDefaultAccount={account.defaultAccount}
                    onSetDefaultAccount={handleSetDefaultAccount}
                    onSetJournalAccount={onSetJournalAccount}
                  />
                ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  function handleSetDefaultAccount(
    accountId: string,
    isCurrentlyDefault: boolean,
  ) {
    onSetDefaultAccount(accountId, isCurrentlyDefault);
  }

  function handleRemoveConnection() {
    onRemove();
  }

  async function handleAddBankAccount() {
    if (!tenantId) return;

    try {
      setAddingAccount(true);
      const response = await addBankAccount(
        connection.bankConnectionId,
        tenantId,
      );

      if (response.status !== 200) {
        toast.error(t("bank_connections.toasts.add_account_error"));
        return;
      }

      const { webformUrl } = response.data ?? {};
      if (!webformUrl) {
        toast.error(t("bank_connections.toasts.add_account_error"));
        return;
      }

      const baseRedirectUrl = `${window.location.origin}/${companySlug}/unternehmen/einstellungen/bank`;
      const successRedirectUrl = encodeURIComponent(
        `${baseRedirectUrl}?addAccountSuccess=true`,
      );
      const errorRedirectUrl = encodeURIComponent(
        `${baseRedirectUrl}?addAccountError=true`,
      );
      const abortRedirectUrl = encodeURIComponent(
        `${baseRedirectUrl}?addAccountAbort=true`,
      );

      window.location.href = `${webformUrl}?redirectUrl=${successRedirectUrl}&errorRedirectUrl=${errorRedirectUrl}&abortRedirectUrl=${abortRedirectUrl}`;
    } catch (error) {
      toast.error(t("bank_connections.toasts.add_account_error"));
    } finally {
      setAddingAccount(false);
    }
  }
};

export const BankConnectionCardSkeleton = () => {
  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden shadow-sm">
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div>
              <Skeleton className="h-4 w-24 mb-1" />
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <Skeleton className="h-3 w-12" />
                <span className="h-1 w-1 rounded-full bg-gray-300" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
          </div>
          <div className="flex items-center">
            <Skeleton className="h-8 w-8 rounded-full" />
            <Skeleton className="h-8 w-8 rounded-full ml-2" />
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-1">
          <Skeleton className="h-4 w-16 rounded" />
        </div>

        <div className="mt-3 flex items-center p-2 rounded-md bg-gray-50 border border-gray-200">
          <Skeleton className="h-4 w-4 mr-2" />
          <div>
            <Skeleton className="h-3 w-20 mb-1" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      </div>
    </div>
  );
};
